import { ArrowLeft, ArrowRight, Check, LockKeyhole } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type CatalogItem } from "../api";
import { ErrorState, InlineNotice, LoadingState } from "../components/Feedback";

export function CheckoutPage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<CatalogItem>();
  const [email, setEmail] = useState("");
  const [accepted, setAccepted] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.product(slug).then(setItem).catch((reason) => setError(reason.message));
  }, [slug]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!accepted || pending) return;
    setError("");
    setPending(true);
    try {
      await api.track("checkout_started", crypto.randomUUID(), { offer_id: slug, displayed_price: item?.priceMxn });
      const result = await api.createOrder(slug, email || undefined);
      if (!result.order.playToken) throw new Error("El acceso ya se procesó antes; vuelve a abrir el checkout para generar una compra de demostración nueva.");
      sessionStorage.setItem(`order:${result.order.orderId}:playToken`, result.order.playToken);
      navigate(`/confirmacion/${result.order.orderId}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos completar el checkout.");
    } finally {
      setPending(false);
    }
  };

  if (!item && !error) return <LoadingState label="Preparando el checkout…" />;
  if (!item) return <ErrorState message={error} />;

  return (
    <div className="checkout-page page-pad">
      <Link className="back-link" to={`/experiencias/${slug}`}><ArrowLeft aria-hidden="true" /> Volver a la experiencia</Link>
      <div className="checkout-layout">
        <form className="checkout-form" onSubmit={submit}>
          <h1>Una noche distinta, en un par de minutos.</h1>
          <InlineNotice tone="info"><strong>Checkout de demostración.</strong> No se solicita tarjeta ni se realiza un cargo real.</InlineNotice>
          <label htmlFor="buyer-email">Correo para recuperar la compra <span>opcional en la demo</span></label>
          <input
            id="buyer-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="tu@correo.mx"
          />
          <label className="checkbox-row">
            <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
            <span>Acepto crear un acceso de prueba y entiendo que esta instalación local no procesa pagos reales.</span>
          </label>
          {error && <InlineNotice tone="error">{error}</InlineNotice>}
          <button className="button button-citron button-large button-full" type="submit" disabled={!accepted || pending}>
            {pending ? "Creando acceso…" : <>Confirmar demo · ${item.priceMxn} MXN <ArrowRight aria-hidden="true" /></>}
          </button>
          <p className="secure-note"><LockKeyhole aria-hidden="true" /> El servidor almacena sólo un hash del correo. El token de juego no entra en analítica.</p>
        </form>
        <aside className="order-summary">
          <span className={`summary-swatch swatch-${item.cover.accent}`} aria-hidden="true" />
          <h2>{item.title}</h2>
          <p>{item.tagline}</p>
          <ul>
            <li><Check aria-hidden="true" /> Acceso por grupo</li>
            <li><Check aria-hidden="true" /> Reanudación de sala</li>
            <li><Check aria-hidden="true" /> Uno o varios teléfonos</li>
          </ul>
          <div className="summary-total"><span>Total de demostración</span><strong>${item.priceMxn} MXN</strong></div>
        </aside>
      </div>
    </div>
  );
}
